import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    Tooltip,
    Typography,
    WithStyles
} from "@material-ui/core";
import { styles } from "./MyReportsView.styles";
import * as UserDetailsAction from "../../../../stores/user/UserDetailsAction";
import * as SubscriptionAction from "../../../../stores/subscriptions/SubscriptionAction";
import { convertDateStringToNumber, mapNumberToDate } from "../../../../utilities/dateTimeUtils";
import CloseIcon from "@material-ui/icons/Close";
import { DATE_FORMAT } from "../../../../constants/TIME";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import { FormDateField } from "../../../components/FormComponents/FormDateField";
import { IIsinOKCheck, IsinTextField } from "../../../components/IsinTextField/IsinTextField";
import { AutocompleteUserDropdown } from "../../../components/FormComponents/AutocompleteUserDropdown";
import { formatPrice } from "../../../../utilities/PriceUtils";
import { MAX_ISINS_FOR_SINGLE_ISIN_REPORT, SINGLE_ISIN_PRODUCT_ID } from "../../../../constants/REPORTS";
import { useGetSubscriptionTypeForProduct } from "../../../../hooks/useGetSubscriptionTypeForProduct";
import SubscriptionUserModel from "../../../../models/subscription/SubscriptionUserModel";

export type ReportDialogType = "createReportByUser" | "createReportOnBehalf";

export interface IReportData {
    userId?: string;
    date?: number;
    isins?: string[];
}

export interface ICreateReportDialogProps extends WithStyles<typeof styles> {
    dialogType: ReportDialogType;
    userId?: string;
    date?: number;
    isins?: string[];
    onConfirm?: (reportData: IReportData) => void;
    onClose?: () => void;
}

const CreateReportDialogPure: React.FunctionComponent<ICreateReportDialogProps> = (props: ICreateReportDialogProps) => {
    const { dialogType, userId, date, isins, onConfirm, onClose, classes } = props;

    const dispatch = useDispatch();

    const [isConfirmationDialogShown, setIsConfirmationDialogShown] = useState<boolean>(false);
    const [isIsinsOK, setIsIsinsOK] = useState<boolean>(false);

    const [actualUser, setActualUser] = useState<string>();
    const [actualDate, setActualDate] = useState<number>(date);
    const [actualIsins, setActualIsins] = useState<string[]>(isins || []);

    const productsInfo = useSelector(state => state.products);
    const singleIsinProduct = productsInfo?.products?.find(p => p.id === SINGLE_ISIN_PRODUCT_ID);
    const singleIsinSubscriptionId = useGetSubscriptionTypeForProduct(SINGLE_ISIN_PRODUCT_ID);
    const unitPrice = singleIsinProduct?.order?.dailyPrice;

    const subscriptionUsersFromState = useSelector(state => state.subscriptions?.subscriptionUsers);

    useEffect(() => {
        if (dialogType === "createReportOnBehalf") {
            dispatch(UserDetailsAction.requestAllUsersDetails());
            dispatch(SubscriptionAction.requestSubscriptionUsers(singleIsinSubscriptionId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let subscriptionUsers;
    if (subscriptionUsersFromState) {
        subscriptionUsers = Object.values(subscriptionUsersFromState)
            ?.filter((user: SubscriptionUserModel) => user.isSubscriptionActive)
            ?.map((user: SubscriptionUserModel) => ({
                userId: user.userId,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email
            }));
    }

    const handleClose = () => {
        onClose();
    };

    interface IDialogTypeConfig {
        title: string;
        confirmButtonTitle: string;
        isConfirmEnabled: () => boolean;
    }

    const dialogTitleMap: {
        [key in ReportDialogType]: IDialogTypeConfig;
    } = {
        createReportByUser: {
            title: "Create a new report",
            confirmButtonTitle: "Send",
            isConfirmEnabled: () => {
                const ret = isIsinsOK && actualDate !== null && actualIsins?.length > 0;
                return ret;
            }
        },
        createReportOnBehalf: {
            title: "Create a new report on user's behalf",
            confirmButtonTitle: "Send",
            isConfirmEnabled: () => {
                const ret = isIsinsOK && actualDate !== null && actualIsins?.length > 0 && actualUser !== undefined;
                return ret;
            }
        }
    };

    const handleIsinChange = (result: IIsinOKCheck) => {
        setIsIsinsOK(result?.result);
        setActualIsins(result?.isins);
    };

    const handleYesClick = () => {
        setIsConfirmationDialogShown(false);
        onConfirm({
            userId: actualUser,
            date: actualDate,
            isins: actualIsins
        });
    };

    const handleConfirmClick = () => {
        setIsConfirmationDialogShown(true);
    };

    const isSendEnabled = dialogTitleMap[dialogType]?.isConfirmEnabled();

    return (
        <>
            <Dialog open fullWidth scroll="paper" onClose={handleClose} maxWidth="md">
                <DialogTitle>
                    <Typography variant="subtitle1">{dialogTitleMap[dialogType]?.title}</Typography>
                    <Typography variant="caption">
                        To create a new report please select a date and enter ISINs{" "}
                    </Typography>
                    <br />
                    <IconButton
                        aria-label="close"
                        style={{ position: "absolute", right: "1px", top: "1px" }}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <FormControl>
                        <div className={classes.sectionInput}>
                            <Grid container spacing={2}>
                                {dialogType === "createReportOnBehalf" && (
                                    <Grid item xs={12}>
                                        <Typography variant={"caption"} gutterBottom>
                                            User
                                        </Typography>
                                        {subscriptionUsers && (
                                            <AutocompleteUserDropdown
                                                allUsers={subscriptionUsers}
                                                selectedUserId={userId}
                                                handleUserChange={user => setActualUser(user?.userId)}
                                            />
                                        )}
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant={"caption"}>Date</Typography>
                                    <FormDateField
                                        placeholder="From"
                                        value={mapNumberToDate(actualDate).format(DATE_FORMAT)}
                                        onChange={newDate => {
                                            setActualDate(convertDateStringToNumber(newDate));
                                        }}
                                        disabled={false}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant={"caption"} gutterBottom>
                                        ISINs
                                    </Typography>
                                    <Typography variant={"body2"} gutterBottom>
                                        Please enter no more than {MAX_ISINS_FOR_SINGLE_ISIN_REPORT} ISINs separated by
                                        a comma or a new line. Alternatively you can also select a comma-separated values (.csv)
                                        or text (.txt) file with the same format.<br />
                                        Note: To identify/request US-ISINs prior March 28th, 2024, please use 9-digit common codes. 
                                    </Typography>
                                    <IsinTextField initialIsins={actualIsins} onChange={handleIsinChange} />
                                </Grid>
                            </Grid>
                        </div>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Tooltip title={!isSendEnabled ? "Please fill all fields" : ""}>
                        <span>
                            <BlueButton onClick={handleConfirmClick} disabled={!isSendEnabled}>
                                {dialogTitleMap[dialogType]?.confirmButtonTitle}
                            </BlueButton>
                        </span>
                    </Tooltip>
                    <BlueButton onClick={onClose}>Cancel</BlueButton>
                </DialogActions>
            </Dialog>

            <Dialog open={isConfirmationDialogShown}>
                <DialogTitle id="confirm-reinitialization-and-clean-dialog">
                    <Typography variant="h4" gutterBottom>
                        Do you want to request a report?
                    </Typography>
                    <Typography variant="caption" paragraph>
                        Each actually processed ISIN/Common code is charged with {formatPrice(unitPrice)} (without VAT)
                        and will be invoiced at the end of the month.
                    </Typography>
                    <Typography variant="caption">
                        Note: Common codes with less than 9 digits will be filled up with zeros at the beginning of the identifier.
                    </Typography>
                </DialogTitle>
                <DialogActions>
                    <BlueButton onClick={handleYesClick}>Yes</BlueButton>
                    <BlueButton onClick={() => setIsConfirmationDialogShown(false)}>No</BlueButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const CreateReportDialog = withStyles(styles)(CreateReportDialogPure);
